import { type FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { motion } from "motion/react";
import { twMerge } from "tailwind-merge";

interface FormInputProps {
  value?: string;
  label: string;
  onChange?: (value?: string) => void;
  format?: (value?: string) => string | undefined;
  isRequired?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  error?: FieldError;
  className?: string;
  maxLength?: number;
}
export const FormInput = ({
  value,
  onChange,
  label,
  isRequired,
  disabled,
  invalid,
  error,
  format,
  className,
  maxLength = 50,
}: FormInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isActive = isOpen || Boolean(value) || invalid;
  const isRequiredError = invalid && error?.type === "required";

  return (
    <div
      onClick={() => {
        setIsOpen(true);
        inputRef.current?.focus();
      }}
      className={twMerge(
        "bg-color-fill text-color-text-3 hover:bg-color-fill-2 active:bg-color-fill-2 flex h-14 w-full cursor-text flex-col justify-center rounded-xl px-4 py-2",
        invalid ? "border border-color-danger! relative z-10 bg-danger-50" : "",
        className,
        disabled ? "text-color-text-2 bg-color-fill-3 cursor-auto" : "",
      )}
    >
      <motion.div
        className={twMerge(
          isActive ? "text-color-text-2 truncate leading-4" : "truncate",
          invalid ? "text-color-danger" : "",
        )}
        style={{ transformOrigin: "top left" }}
        animate={{
          scale: isActive ? 0.85 : 1,
        }}
        transition={{ duration: 0.1, ease: "linear" }}
      >
        {`${label}${isRequiredError ? t("v_required") : ""}`}
      </motion.div>
      <motion.div
        className={twMerge(
          "text-color-text-1 ",
          isRequiredError ? "text-color-text-4" : "",
        )}
        animate={{ height: isActive ? 21 : 0 }}
        transition={{ duration: 0.1, ease: "linear" }}
      >
        <input
          ref={inputRef}
          value={format ? format(value) : value}
          disabled={disabled}
          placeholder={error?.message}
          className={twMerge(
            "block w-full focus-visible:outline-none !outline-solid outline-transparent bg-transparent h-5 leading-5 placeholder:text-color-text-4",
            isActive ? "h-auto" : "h-0",
          )}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          onChange={(e) => {
            if (e.target.value?.length <= maxLength) {
              onChange?.(e.target.value);
            }
          }}
        />
      </motion.div>
    </div>
  );
};
